"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import PhotoSwipeLightbox from "photoswipe/lightbox";
import "photoswipe/style.css";

import {
  adminCommonFileDelete,
  adminCommonFileDownload,
  adminCommonFileImageRotate,
  adminCommonFilesSort,
  adminCommonFilesUpdate,
} from "../api/adminQuery";
import { getFileUrl } from "@/shared";

interface Props {
  queryKey: string;
  table: string;
  column: string;
  id: string;
  timestamp: string;
  files: any[];
  accept?: string;
}

export function FilesEditUploader({
  queryKey,
  table,
  column,
  id,
  timestamp,
  files,
  accept,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const queryClient = useQueryClient();
  const updateMutation = useMutation({
    mutationFn: adminCommonFilesUpdate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      toast.success(`파일이 업로드되었습니다.`);
    },
    onError: (e: any) => {
      toast.error(`에러(${e.message})가 발생했습니다. 다시 시도해 주세요.`);
    },
  });
  const onUploadFile = async (e: any) => {
    e.preventDefault();

    try {
      if (updateMutation.isPending) return;

      const files = e.target.files;

      await updateMutation.mutateAsync({
        files,
        timestamp,
        table,
        column,
        id,
      });
    } catch (e) {
      console.log(e);
      toast.error("에러가 발생했습니다. 다시 시도해 주세요.");
    }
  };
  const onClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const deleteMutation = useMutation({
    mutationFn: adminCommonFileDelete,
    onSuccess: () => {
      toast.success(`파일이 삭제되었습니다.`);
      queryClient.invalidateQueries({ queryKey: [queryKey] });
    },
    onError: (e: any) => {
      toast.error(`에러(${e.message})가 발생했습니다. 다시 시도해 주세요.`);
    },
  });
  const onDeleteFile = async (file: any) => {
    try {
      if (!window.confirm("정말 삭제하시겠습니까?")) return;
      if (deleteMutation.isPending) return;

      await deleteMutation.mutateAsync({ file });
    } catch (e) {
      console.log(e);
      toast.error("에러가 발생했습니다. 다시 시도해 주세요.");
    }
  };

  const [isDownLoading, setIsDownLoading] = useState<boolean>(false);
  const downloadMutation = useMutation({
    mutationFn: adminCommonFileDownload,
    onSuccess: () => {
      toast.success(`파일이 다운로드되었습니다.`);
    },
    onError: (e: any) => {
      toast.error(`에러(${e.message})가 발생했습니다. 다시 시도해 주세요.`);
    },
  });
  const onDownloadFile = useCallback(async (file: any) => {
    try {
      setIsDownLoading(true);
      if (downloadMutation.isPending) return;

      const { result } = await downloadMutation.mutateAsync({
        common_file_id: file.id,
      });

      const blobUrl = window.URL.createObjectURL(result as Blob);
      const link = document.createElement("a");

      link.setAttribute("href", blobUrl);
      link.setAttribute("download", file.name);
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.log(err);
    } finally {
      setIsDownLoading(false);
    }
  }, []);

  const [isRotating, setIsRotating] = useState<boolean>(false);
  const rotateMutation = useMutation({
    mutationFn: adminCommonFileImageRotate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      toast.success(`이미지가 회전되었습니다.`);
    },
    onError: (e: any) => {
      toast.error(`에러(${e.message})가 발생했습니다. 다시 시도해 주세요.`);
    },
  });
  const onRetateFile = useCallback(async (id: any, angle: string) => {
    try {
      setIsRotating(true);
      if (rotateMutation.isPending) return;

      await rotateMutation.mutateAsync({ id, angle });
    } catch (e) {
      console.log(e);
    } finally {
      setIsRotating(false);
    }
  }, []);

  const sortMutation = useMutation({
    mutationFn: adminCommonFilesSort,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      toast.success(`이미지가 이동되었습니다.`);
    },
    onError: (e: any) => {
      toast.error(`에러(${e.message})가 발생했습니다. 다시 시도해 주세요.`);
    },
  });
  const onSortFile = async (file: any, sort: string) => {
    // console.log("onSort", {
    //   file,
    //   table,
    //   column,
    //   id,
    //   common_file_id: file.id,
    //   sort,
    // });
    // return;

    try {
      if (sortMutation.isPending) return;

      await sortMutation.mutateAsync({
        table,
        column,
        id,
        common_file_id: file.id,
        sort,
      });
    } catch (e) {
      console.log(e);
      toast.error("에러가 발생했습니다. 다시 시도해 주세요.");
    }
  };

  useEffect(() => {
    const lightbox = new PhotoSwipeLightbox({
      gallery: `#${column}`,
      children: "a",
      pswpModule: () => import("photoswipe"),
    });
    lightbox.init();

    return () => lightbox.destroy();
  }, []);

  return (
    <>
      <div className="coverUpload-list-wrap bg-gray-100" id={column}>
        <div className="coverUpload-list">
          <input
            ref={fileInputRef}
            type="file"
            onChange={onUploadFile}
            className="hide"
            accept={accept ?? accept}
            multiple
          />
          <button type="button" className="upload-btn" onClick={onClick}>
            <i className="fa fa-upload fa-lg"></i>
            <br />
            업로드
          </button>
        </div>
        {files.map((file, index) => {
          return (
            <div className="coverUpload-list" key={index}>
              <div className="preview w-200px">
                <div
                  className="img"
                  style={{
                    backgroundImage: file?.type.startsWith("image/")
                      ? `url("${getFileUrl(file?.file_url)}")`
                      : `url("/assets/images/default_product.png")`,
                  }}
                />
                <div className="preview_button">
                  <div className="flex">
                    <div className="w-20 text-ellipsis overflow-hidden text-center fw-bold text-white">
                      {file.name}
                    </div>
                    <div className="flex flex-row py-1">
                      <button
                        className="btn btn-white btn-xs basis-1/2 mx-1"
                        type="button"
                        onClick={() => onDeleteFile(file)}
                      >
                        <i className="fa fa-trash"></i>
                        {/* 삭제 */}
                      </button>
                      <button
                        className="btn btn-white btn-xs basis-1/2 mx-1"
                        type="button"
                        disabled={isDownLoading}
                        onClick={() => onDownloadFile(file)}
                      >
                        <i className="fa fa-download"></i>
                        {/* 다운 */}
                      </button>
                      {file?.type.startsWith("image/") && (
                        <a
                          href={getFileUrl(file?.file_url)}
                          data-pswp-width={file.width}
                          data-pswp-height={file.height}
                          className="btn btn-white btn-xs basis-1/2 mx-1 preview"
                        >
                          <i className="fa fa-magnifying-glass"></i>
                          {/* 확대 */}
                        </a>
                      )}
                    </div>
                    <div className="flex flex-row py-1">
                      <button
                        className="btn btn-white btn-xs basis-1/2 mx-1"
                        type="button"
                        onClick={() => onSortFile(file, "upper")}
                      >
                        <i className="fa fa-arrow-left"></i>
                        {/* 편집 */}
                      </button>
                      <button
                        className="btn btn-white btn-xs basis-1/2 mx-1"
                        type="button"
                        onClick={() => onSortFile(file, "lower")}
                      >
                        <i className="fa fa-arrow-right"></i>
                        {/* 편집 */}
                      </button>
                    </div>
                    {file?.type.startsWith("image/") && (
                      <div className="flex flex-row py-1">
                        <button
                          className="btn btn-white btn-xs basis-1/2 mx-1"
                          type="button"
                          disabled={isRotating}
                          onClick={() => onRetateFile(file.id, "-90")}
                        >
                          <i className="fa fa-rotate-left"></i>
                          {/* 회전 */}
                        </button>
                        <button
                          className="btn btn-white btn-xs basis-1/2 mx-1"
                          type="button"
                          disabled={isRotating}
                          onClick={() => onRetateFile(file.id, "90")}
                        >
                          <i className="fa fa-rotate-right"></i>
                          {/* 회전 */}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="coverUpload-box">
        <div className="coverUpload-info">
          <p className="mb-0 fs-10px">파일 : .png, .jpg, .jpeg, .xlsx, xls</p>
          <p className="mb-0 fs-10px">용량 : 50MB 이하로 업로드해주세요.</p>
        </div>
      </div>
    </>
  );
}
